import type { BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import type { Porch } from "building-types/porches/porch";
import { PorchKeyMeasuresGeometryType } from "building-types/porches/porchKeyMeasuresTypes";
import type { PropAttributeTable } from "building-types/props/propTypes";
import type { Table } from "common/data/table";
import { materialSrcFromPalette } from "doc/materialLookup";
import { faceCategoryFromPropCategory } from "doc/propTools";
import { faceGroupNew } from "geom/brep";
import { geomToFaces } from "geom/geom";
import { buildExteriorLayers, emitBuildingProp } from "./buildingGeometryTools";

/**
 * Generate BuildingGeometry for porches.
 */
export function getPorchGeometries(
  porches: Table<Porch>,
  exteriorPalette: ExteriorPalette,
  emitter: BuildingGeometryEmitter,
  propCatalog?: PropAttributeTable,
): void {
  const threeLayers = buildExteriorLayers();

  for (const porchId in porches) {
    const porch = porches[porchId];
    const { group, rooms } = porch;
    const { geom, tags } = group;

    // Use the level from the first room - all rooms in a porch should be on the same level
    const levelId = rooms[0]?.level.id;

    const entityHandleForSelectingReportingUnit = roomEntityHandleForSelectingReportingUnit(rooms[0]);

    for (let i = 0, l = geom.length; i < l; i++) {
      const geo = geom[i];
      const { exteriorMaterial, uvGen, viewEntityDescriptor, materialTransformation } = tags[i];

      // porches have several architectural components that should be collected
      // separately (structure, finishes, etc) so we skip this here for now
      if (tags[i].porchKeyMeasuresGeometryType === PorchKeyMeasuresGeometryType.COLUMN_STRUCTURE) {
        continue;
      }

      const materialSrc = exteriorMaterial ? materialSrcFromPalette(exteriorPalette, exteriorMaterial) : undefined;
      const faces = geomToFaces(geo);

      emitter.emit({
        geom: faceGroupNew(faces),
        faceCategory: FaceCategory.PORCH_DECORATION,
        threeLayers,
        levelId,
        entityId: viewEntityDescriptor?.entityId,
        entityHandleForSelectingReportingUnit,
        descriptor: viewEntityDescriptor,
        materialSlot: exteriorMaterial,
        materialSrc,
        uvGen,
        materialTransformation,
        pickHandle: porch.pickHandle,
      });
    }

    // Emit porch props
    if (propCatalog && porch.props) {
      for (const prop of porch.props) {
        const propAttributes = propCatalog[prop.src];
        const faceCategory = faceCategoryFromPropCategory(propAttributes?.category);

        emitBuildingProp(emitter, prop, {
          threeLayers,
          faceCategory,
          propCatalog,
          levelId,
          entityHandleForSelectingReportingUnit,
          pickHandle: porch.pickHandle,
        });
      }
    }
  }
}
